import React, { useEffect, useState } from 'react';
import { CircularProgress, Tooltip, makeStyles } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import SettingsIcon from '@material-ui/icons/Settings';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { whatsappOficialApi } from 'api/whatsappOficialApi';
import { getTenantId } from 'util/isAdmin';
import { SETTINGS_VIEW_TEMPLATE_CONFIG } from '../../utils/whatsappSIdebarTabs';
import { getReferenceLabel } from '../../utils/whatsappTemplates';
import { useMessage } from '../../context/MessageContext';
import QualityBadge from './QualityBadge';
import * as S from './styles';

const QUALITY_BELOW_GOOD = [ 'YELLOW', 'RED' ];

const useTooltipStyles = makeStyles( () => ( {
	tooltip: {
		fontSize: '13px',
		lineHeight: '1.4',
		padding: '10px 14px',
		maxWidth: 300,
		backgroundColor: '#1f2937',
		color: '#fff',
		boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
	},
	arrow: {
		color: '#1f2937',
	},
} ) );

const InfoTooltip = ( { title } ) => {
	const classes = useTooltipStyles();
	return (
		<Tooltip title={title} arrow placement="top" classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}>
			<S.InfoIcon>
				<InfoOutlinedIcon />
			</S.InfoIcon>
		</Tooltip>
	);
};

const TemplateNameCell = ( { name } ) => {
	const classes = useTooltipStyles();
	return (
		<Tooltip title={name} arrow placement="top" classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}>
			<S.TemplateName>{name}</S.TemplateName>
		</Tooltip>
	);
};

const LabelInfo = ( { label, title } ) => (
	<S.LabelWithInfo>
		{label}
		<InfoTooltip title={title} />
	</S.LabelWithInfo>
);

const TooltipStatusList = ( { intro, items } ) => (
	<div>
		{intro && <div style={{ marginBottom: 6 }}>{intro}</div>}
		{items.map( ( { code, description } ) => (
			<div key={code} style={{ marginTop: 2 }}>
				<strong>{code}</strong>: {description}
			</div>
		) )}
	</div>
);

const mergeTemplates = ( healthTemplates, dbTemplates ) => {
	const dbByName = new Map( dbTemplates.map( t => [ t.name, t ] ) );
	return healthTemplates.map( template => ( {
		...template,
		id: dbByName.get( template.name )?.id ?? null,
		reference: dbByName.get( template.name )?.reference ?? template.name,
	} ) );
};

const WhatsappHealth = () => {
	const { setSettingsView, setSettingsTemplate } = useMessage();
	const [ data, setData ] = useState( null );
	const [ templates, setTemplates ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( '' );
	const [ syncing, setSyncing ] = useState( false );
	const [ syncStatus, setSyncStatus ] = useState( '' );
	const [ campaignPaused, setCampaignPaused ] = useState( false );
	const [ campaignUpdating, setCampaignUpdating ] = useState( false );
	const [ campaignActionStatus, setCampaignActionStatus ] = useState( '' );

	const load = async () => {
		setLoading( true );
		setError( '' );
		try {
			const [ healthRes, templatesRes, campaignRes ] = await Promise.all( [
				whatsappOficialApi.getHealth(),
				whatsappOficialApi.getTemplates(),
				whatsappOficialApi.getCampaignStatus( getTenantId() ),
			] );
			setData( healthRes.data );
			setTemplates( mergeTemplates( healthRes.data?.templates ?? [], templatesRes.data.templates ?? [] ) );
			setCampaignPaused( !!campaignRes.data?.paused );
		} catch ( e ) {
			console.error( 'WhatsappHealth.load error', e );
			setError( 'Erro ao consultar saúde na Meta. Tente novamente.' );
		} finally {
			setLoading( false );
		}
	};

	useEffect( () => {
		load();
	}, [] );

	const handleToggleCampaign = async () => {
		const willPause = !campaignPaused;
		const confirmMessage = willPause
			? 'Pausar a campanha impede o envio de novos templates para este tenant até que seja retomada. Confirma?'
			: 'Retomar a campanha volta a permitir o envio de templates para este tenant. Confirma?';

		if ( !window.confirm( confirmMessage ) ) {
			return;
		}

		setCampaignUpdating( true );
		setCampaignActionStatus( '' );
		try {
			if ( willPause ) {
				await whatsappOficialApi.pauseCampaign( getTenantId() );
			} else {
				await whatsappOficialApi.resumeCampaign( getTenantId() );
			}
			setCampaignPaused( willPause );
			setCampaignActionStatus( willPause ? 'Campanha pausada.' : 'Campanha retomada.' );
		} catch ( e ) {
			console.error( 'WhatsappHealth.toggleCampaign error', e );
			setCampaignActionStatus( 'Erro ao atualizar status da campanha.' );
		} finally {
			setCampaignUpdating( false );
		}
	};

	const handleOpenConfig = ( template ) => {
		setSettingsTemplate( template );
		setSettingsView( SETTINGS_VIEW_TEMPLATE_CONFIG );
	};

	const handleSync = async () => {
		setSyncing( true );
		setSyncStatus( '' );
		try {
			const res = await whatsappOficialApi.syncTemplates();
			const total = res.data.results?.reduce( ( acc, r ) => acc + ( r.total ?? 0 ), 0 ) ?? 0;
			setSyncStatus( `Sincronizado: ${total} template(s) encontrado(s)` );
			await load();
		} catch ( e ) {
			setSyncStatus( 'Erro ao sincronizar' );
		} finally {
			setSyncing( false );
		}
	};

	return (
		<S.HealthContainer>
			<S.HeaderContainer>
				<div>
					<S.LabelWithInfo>
						<S.Title>Saúde do WhatsApp</S.Title>
						<InfoTooltip title="Esta tela mostra se o número está bem visto pela Meta (dono do WhatsApp) e se as mensagens automáticas (templates) estão liberadas para envio. Se algo aparecer em vermelho ou amarelo, os envios podem ser bloqueados ou limitados." />
					</S.LabelWithInfo>
					<S.Subtitle>Qualidade do número e dos templates na Meta</S.Subtitle>
				</div>
				<S.RefreshButton onClick={load} disabled={loading}>
					{loading ? 'Atualizando...' : 'Atualizar'}
				</S.RefreshButton>
			</S.HeaderContainer>

			{loading && <S.StatusText>Carregando...</S.StatusText>}

			{!loading && error && <S.StatusText>{error}</S.StatusText>}

			{!loading && !error && data && !data.hasMetaApi && (
				<S.StatusText>Meta API não configurada para este tenant.</S.StatusText>
			)}

			{!loading && !error && data?.hasMetaApi && (
				<>
					<S.PhoneCard>
						<S.PhoneNumberText>{data.phoneNumber?.displayPhoneNumber ?? '—'}</S.PhoneNumberText>

						<S.InfoRow>
							<LabelInfo
								label="Campanha"
								title="Envio de templates (mensagens automáticas) para clientes. Pausar interrompe o disparo imediatamente; você pode retomar quando quiser."
							/>
							<S.CampaignActionButton
								$paused={campaignPaused}
								disabled={campaignUpdating}
								onClick={handleToggleCampaign}
							>
								{campaignUpdating
									? <CircularProgress size={12} style={{ color: 'inherit' }} />
									: campaignPaused ? <PlayCircleOutlineIcon style={{ fontSize: 16 }} /> : <PauseCircleOutlineIcon style={{ fontSize: 16 }} />}
								{campaignPaused ? 'Retomar' : 'Pausar'}
							</S.CampaignActionButton>
						</S.InfoRow>

						{campaignPaused && QUALITY_BELOW_GOOD.includes( data.phoneNumber?.qualityRating ) && (
							<S.StatusText style={{ marginLeft: 0 }}>
								Campanha pausada devido à queda na qualidade do número. Você pode retomar o envio quando quiser.
							</S.StatusText>
						)}
						{campaignActionStatus && (
							<S.StatusText style={{ marginLeft: 0 }}>{campaignActionStatus}</S.StatusText>
						)}

						<S.InfoRow>
							<LabelInfo
								label="Qualidade"
								title="Reputação do número junto ao WhatsApp, baseada em como as pessoas reagem às mensagens (bloqueios, denúncias, respostas). Boa: envios normais. Média: preste atenção, pode ser reduzido. Ruim: risco de bloqueio dos envios."
							/>
							<QualityBadge status={data.phoneNumber?.qualityRating} />
						</S.InfoRow>
						<S.InfoRow>
							<LabelInfo
								label="Limite de mensagens"
								title="Quantidade de conversas que este número pode iniciar por dia com clientes novos. Esse limite é definido pela Meta e aumenta conforme o número mantém boa qualidade."
							/>
							<span>{data.phoneNumber?.messagingLimitTier ?? '—'}</span>
						</S.InfoRow>
						<S.InfoRow>
							<LabelInfo
								label="Status do nome"
								title={
									<TooltipStatusList
										intro="Situação da aprovação do nome de exibição do WhatsApp Business pela Meta."
										items={[
											{ code: 'APPROVED', description: 'nome revisado e aprovado.' },
											{ code: 'AVAILABLE_WITHOUT_REVIEW', description: 'nome liberado sem revisão manual (também está OK).' },
											{ code: 'PENDING_REVIEW', description: 'em análise.' },
											{ code: 'DECLINED', description: 'nome rejeitado.' },
										]}
									/>
								}
							/>
							<span>{data.phoneNumber?.nameStatus ?? '—'}</span>
						</S.InfoRow>
						<S.InfoRow>
							<LabelInfo
								label="Verificação"
								title="Status da verificação de segurança do número na Meta. EXPIRED significa que a verificação venceu e pode ser necessário revalidar o número."
							/>
							<span>{data.phoneNumber?.codeVerificationStatus ?? '—'}</span>
						</S.InfoRow>
					</S.PhoneCard>

					<S.SectionHeader>
						<S.SectionTitle>Templates</S.SectionTitle>
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							{syncStatus && <S.StatusText>{syncStatus}</S.StatusText>}
							<S.SyncButton onClick={handleSync} disabled={syncing}>
								{syncing ? 'Sincronizando...' : 'Sincronizar templates'}
							</S.SyncButton>
						</div>
					</S.SectionHeader>

					{templates.length === 0 ? (
						<S.StatusText>Nenhum template encontrado. Clique em &ldquo;Sincronizar templates&rdquo; para buscar da Meta.</S.StatusText>
					) : (
						<S.TableWrapper>
							<S.TableHeader>
								<span>Nome do template</span>
								<LabelInfo
									label="Categoria"
									title="Tipo de conteúdo do template, definido pela Meta (ex.: Marketing, Utilidade, Autenticação). Isso influencia regras e custos de envio."
								/>
								<LabelInfo
									label="Status"
									title={
										<TooltipStatusList
											intro="Situação de aprovação do template pela Meta."
											items={[
												{ code: 'APPROVED', description: 'já pode ser usado para enviar mensagens.' },
												{ code: 'PENDING', description: 'em análise, ainda não pode ser usado.' },
												{ code: 'REJECTED', description: 'reprovado, não pode ser usado.' },
											]}
										/>
									}
								/>
								<LabelInfo
									label="Qualidade"
									title={
										<TooltipStatusList
											intro="Avaliação do template com base no feedback de quem recebe as mensagens."
											items={[
												{ code: 'Boa', description: 'engajamento saudável.' },
												{ code: 'Média', description: 'atenção, pode ser reduzida.' },
												{ code: 'Ruim', description: 'risco de a Meta pausar o envio desse template.' },
												{ code: 'Desconhecida', description: 'ainda sem dados suficientes.' },
											]}
										/>
									}
								/>
								<LabelInfo
									label="Tipo no sistema"
									title="Define para qual fluxo do sistema (ex.: proposta, cobrança) este template será usado quando o sistema disparar mensagens automáticas."
								/>
								<S.CenteredHeader>Configurar</S.CenteredHeader>
							</S.TableHeader>
							{templates.map( template => (
								<S.TableRow key={template.name}>
									<TemplateNameCell name={template.name} />
									<S.CellText>{template.category ?? '—'}</S.CellText>
									<S.CellText>{template.status}</S.CellText>
									<QualityBadge status={template.qualityScore} date={template.qualityScoreDate} />
									{template.reference === template.name
										? <S.UnmappedText>— sem mapeamento —</S.UnmappedText>
										: <S.ReferenceText>{getReferenceLabel( template.reference )}</S.ReferenceText>}
									<S.ConfigCell>
										<S.ConfigButton
											type="button"
											aria-label={`Configurar template ${template.name}`}
											onClick={() => handleOpenConfig( template )}
											disabled={!template.id}
										>
											<SettingsIcon />
										</S.ConfigButton>
									</S.ConfigCell>
								</S.TableRow>
							) )}
						</S.TableWrapper>
					)}
				</>
			)}
		</S.HealthContainer>
	);
};

export default WhatsappHealth;
